// TagInput — combobox de tags controlado. Cria tag (Enter ou vírgula), remove
// chip (botão x ou Backspace no campo vazio) e sugere de uma lista filtrada.
//
// SRP: só edita um array de strings. Não conhece domínio (contato, loja…) —
// quem decide o que fazer com `value` é o consumidor. A11y: combobox + listbox
// com aria-activedescendant, navegação por teclado (↑/↓/Enter/Esc/Backspace).
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils/cn';
import styles from './TagInput.module.css';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Sugestões oferecidas no dropdown (as já presentes em `value` somem). */
  suggestions?: string[];
  placeholder?: string;
  /** Label acessível pro campo. Default "Tags". */
  label?: string;
  /** Limite de tags. Sem limite por default. */
  max?: number;
  id?: string;
  className?: string;
}

function normalize(tag: string): string {
  return tag.trim();
}

export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Adicionar tag…',
  label = 'Tags',
  max,
  id,
  className,
}: TagInputProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const listId = `${inputId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const atMax = max !== undefined && value.length >= max;

  const filtered = useMemo(() => {
    const taken = new Set(value.map((t) => t.toLowerCase()));
    const q = draft.trim().toLowerCase();
    return suggestions.filter(
      (s) => !taken.has(s.toLowerCase()) && (q === '' || s.toLowerCase().includes(q)),
    );
  }, [suggestions, value, draft]);

  const showList = open && filtered.length > 0;

  const addTag = (raw: string) => {
    const tag = normalize(raw);
    if (tag === '' || atMax) return;
    const exists = value.some((t) => t.toLowerCase() === tag.toLowerCase());
    if (!exists) onChange([...value, tag]);
    setDraft('');
    setHighlight(-1);
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlight >= 0 && highlight < filtered.length) {
        addTag(filtered[highlight]);
      } else {
        addTag(draft);
      }
      return;
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      removeTag(value.length - 1);
      return;
    }
    if (e.key === 'ArrowDown' && filtered.length > 0) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % filtered.length);
      return;
    }
    if (e.key === 'ArrowUp' && filtered.length > 0) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h <= 0 ? filtered.length - 1 : h - 1));
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const activeOptionId = highlight >= 0 ? `${listId}-opt-${highlight}` : undefined;

  return (
    <div className={cn(styles.root, className)}>
      <span id={`${inputId}-label`} className={styles.label}>
        {label}
      </span>
      <div className={styles.control}>
        <ul className={styles.chips} aria-label="Tags selecionadas">
          {value.map((tag, i) => (
            <li key={tag} className={styles.chip}>
              <span className={styles.chipLabel}>{tag}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeTag(i)}
                aria-label={`Remover tag ${tag}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          aria-labelledby={`${inputId}-label`}
          className={styles.input}
          placeholder={atMax ? 'Limite atingido' : placeholder}
          value={draft}
          disabled={atMax}
          onChange={(e) => {
            setDraft(e.currentTarget.value);
            setOpen(true);
            setHighlight(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>
      <ul id={listId} role="listbox" className={cn(styles.list, !showList && styles.hidden)}>
        {showList &&
          filtered.map((s, i) => (
            <li
              key={s}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={cn(styles.option, i === highlight && styles.optionActive)}
              // onMouseDown (não onClick): roda antes do blur do input, senão o
              // dropdown fecha e o clique se perde.
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
            >
              {s}
            </li>
          ))}
      </ul>
    </div>
  );
}

export default TagInput;
